import { type RefObject, useLayoutEffect } from 'react';

/**
 * Adopts a stylesheet into whichever root the element renders in — the shadow root under
 * MalachiteContext, or the document.
 *
 * Components need this for rules that cannot be expressed inline: keyframes, media queries,
 * `:has()`, pseudo-elements. The sheet is constructed once per `id` and shared across every
 * instance, and falls back to a `<style>` tag where `adoptedStyleSheets` is unavailable.
 */
const sheets = new Map<string, CSSStyleSheet | null>();

const ensureSheet = (id: string, css: string): CSSStyleSheet | null => {
    if (sheets.has(id)) {
        return sheets.get(id) ?? null;
    }
    if (typeof CSSStyleSheet === 'undefined') {
        sheets.set(id, null);
        return null;
    }
    try {
        const sheet = new CSSStyleSheet();
        sheet.replaceSync(css);
        sheets.set(id, sheet);
        return sheet;
    } catch (error) {
        console.error(`Failed to construct the ${id} stylesheet:`, error);
        sheets.set(id, null);
        return null;
    }
};

const adoptSheet = (root: Document | ShadowRoot, sheet: CSSStyleSheet): boolean => {
    if (!('adoptedStyleSheets' in root)) {
        return false;
    }
    try {
        if (!root.adoptedStyleSheets.includes(sheet)) {
            root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet];
        }
        return true;
    } catch {
        return false;
    }
};

const appendStyleTag = (root: Document | ShadowRoot, id: string, css: string): void => {
    const existing =
        root instanceof Document ? root.getElementById(id) : root.querySelector(`#${id}`);
    if (existing) {
        return;
    }
    const style = document.createElement('style');
    style.id = id;
    style.textContent = css;
    if (root instanceof Document) {
        root.head.appendChild(style);
    } else {
        root.appendChild(style);
    }
};

export const useAdoptedStyleSheet = (
    ref: RefObject<HTMLElement | null>,
    css: string,
    id: string,
): void => {
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) {
            return;
        }
        const root = el.getRootNode();
        if (!(root instanceof Document) && !(root instanceof ShadowRoot)) {
            return;
        }
        const sheet = ensureSheet(id, css);
        if (!sheet || !adoptSheet(root, sheet)) {
            appendStyleTag(root, id, css);
        }
    }, [ref, css, id]);
};

export default useAdoptedStyleSheet;
